import { useState, useEffect, useMemo } from 'react';
import { Wallet, DollarSign, AlertTriangle, Clock } from 'lucide-react';
import client from '../../api/client';
import { usePaginatedSort } from '../../shared/hooks/usePaginatedSort';
import PaginationBar from '../../shared/components/PaginationBar';
import SortableHeader from '../../shared/components/SortableHeader';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle, fieldStyle, selectStyle } from '../../styles/tokens';

/**
 * SALDOS Y COSTO — el dashboard AMPLIADO (V62). Socio · Contable · Admin.
 *
 * Es el dashboard común MÁS los saldos de toda la población y el costo del pasivo. Vive en
 * su propia RUTA y no como una sección condicional adentro del común: así el gate es una
 * línea y no hay riesgo de que media pantalla se filtre a quien no corresponde.
 *
 * ⚠⚠ EL COSTO ES SOLO DE VACACIONES, Y LA ETIQUETA LO DICE. La enfermedad se muestra en la
 * tabla de saldos pero NO se suma: al inactivar a alguien su saldo de enfermedad puede ir a
 * 0 SIN pagarse, mientras el de vacaciones se mantiene. Un saldo que se reinicia sin
 * pagarse no es pasivo, y sumarlo inflaría el número que se usa para provisionar.
 *
 * ⚠ `hourly_rate` NULL no es 0: esas filas muestran "—" y se cuentan aparte. Un 0 en la
 * columna de dinero es indistinguible de un costo real de cero.
 *
 * Layout tomado como REFERENCIA de `VacacionesDashboardPage`/`VacacionesLiabilityPage` de
 * `feature/vacaciones-module` (KPIs arriba, tabla por persona abajo). Se leyó, no se
 * mergeó: aquella versión gatea con `users.can_view_financials`, una columna que no existe
 * en main, y lee de un esquema anterior al de V52.
 */

/**
 * ⚠ LOS NEGATIVOS VAN ENTRE PARÉNTESIS Y EN ROJO, como en el Excel de la contable: ahí se
 * ven así —($126.92), ($6,292.00)— y se SUMAN al total. Un saldo negativo es tiempo
 * adelantado que el empleado debe, o sea que REDUCE el pasivo neto. Escribirlo con un
 * signo menos chiquito lo vuelve fácil de pasar por alto justo en la columna de dinero.
 */
const money = (n) => {
  if (n === null || n === undefined) return '—';
  const v = Number(n);
  const abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `($${abs})` : `$${abs}`;
};
const moneyColor = (n) => (n === null || n === undefined) ? '#94a3b8' : (Number(n) < 0 ? '#dc2626' : '#16a34a');
const horas = (n) => `${(Number(n) || 0).toFixed(2)}`;
const horasColor = (n) => (Number(n) < 0 ? '#dc2626' : NAVY);

function Kpi({ Icono, label, valor, sub, color = NAVY }) {
  return (
    <div style={{ ...bubbleStyle, padding: '14px 18px', flex: '1 1 200px', minWidth: 200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Icono size={15} color={color} />
        <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color, lineHeight: 1.1 }}>{valor}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

export default function BalancesPage() {
  const añoActual = new Date().getFullYear();
  const [year, setYear] = useState(añoActual);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    client.get(`/api/vacation/balances-report?year=${year}`)
      .then(r => { setData(r.data); setError(''); })
      .catch(err => setError(err.response?.data?.error || 'No se pudo cargar el reporte.'))
      .finally(() => setLoading(false));
  }, [year]);

  // Se aplana para que el hook pueda ordenar por columnas anidadas.
  const filas = useMemo(() => (data?.rows || []).map(r => ({
    ...r,
    vac_balance: r.vacation.balance,
    vac_reserved: r.vacation.reserved,
    vac_available: r.vacation.available,
    enf_balance: r.sick.balance,
  })), [data]);

  const term = search.trim().toLowerCase();
  const filtradas = useMemo(() => (
    term
      ? filas.filter(r => r.name.toLowerCase().includes(term)
          || String(r.external_id || '').toLowerCase().includes(term))
      : filas
  ), [filas, term]);

  // ⚠ El hook corre SIEMPRE, antes de cualquier return temprano: si quedara después del
  // `if (loading)`, la cantidad de hooks cambiaría entre renders (el bug de V29.2).
  const {
    pageItems, page, pageSize, totalPages, totalItems,
    sortKey, sortDir, toggleSort, setPage, setPageSize,
  } = usePaginatedSort(filtradas, { defaultSortKey: 'name' });

  const th = { padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#fff', background: NAVY, textAlign: 'left' };
  const td = { padding: '7px 10px', fontSize: 12.5, color: NAVY, borderBottom: `1px solid ${BORDER_COLOR}` };
  const tdNum = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

  return (
    <div className="page-pad" style={{ padding: '20px 24px', maxWidth: 1180, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: 0 }}>Saldos y Costo</h1>
        <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...selectStyle, width: 'auto' }}>
          {[añoActual + 1, añoActual, añoActual - 1].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {error && (
        <div style={{ ...bubbleStyle, padding: '12px 16px', marginBottom: 16, background: '#fef2f2', color: '#991b1b' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando…</div>
      ) : !data ? null : (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <Kpi
              Icono={DollarSign} color={moneyColor(data.totals.net_total)}
              label="Pasivo neto"
              valor={money(data.totals.net_total)}
              sub={`${data.totals.with_rate} con tarifa · ${data.totals.personas} personas`}
            />
            {/* ⚠⚠ EL BRUTO AL LADO DEL NETO, Y NO ES ADORNO: un descubierto grande baja el
                neto y alguien puede leer un pasivo MENOR del real. Mostrar adeudado y
                adelantado por separado hace visible la compensación en vez de esconderla. */}
            <Kpi
              Icono={Wallet} color={moneyColor(data.totals.vacation.net)}
              label="Vacaciones"
              valor={money(data.totals.vacation.net)}
              sub={`adeudado ${money(data.totals.vacation.positive)} · adelantado ${money(data.totals.vacation.negative)}`}
            />
            <Kpi
              Icono={Clock} color={moneyColor(data.totals.sick.net)}
              label="Enfermedad — solo activos"
              valor={money(data.totals.sick.net)}
              sub={`adeudado ${money(data.totals.sick.positive)} · adelantado ${money(data.totals.sick.negative)}`}
            />
            {data.totals.without_rate > 0 && (
              <Kpi
                Icono={AlertTriangle} color="#a16207" label="Sin tarifa"
                valor={data.totals.without_rate}
                sub="su costo no se puede calcular"
              />
            )}
          </div>

          {/* ⚠ La aclaración va PEGADA al número, no en un pie de página: el costo se lee
              de un vistazo y hay que poder ver de qué es sin buscar. */}
          <div style={{
            ...bubbleStyle, padding: '10px 14px', marginBottom: 16,
            background: '#f8fafc', fontSize: 12, color: '#475569', lineHeight: 1.6,
          }}>
            <strong>Cómo se calcula.</strong> Las <strong>vacaciones</strong> cuentan siempre
            —también para inactivos: no se liquidan automáticamente, quedan por si la persona
            vuelve—. La <strong>enfermedad</strong> cuenta <strong>solo para activos</strong>:
            al inactivar, ese saldo se reinicia sin pagarse, así que deja de ser pasivo.
            Los saldos <span style={{ color: '#dc2626', fontWeight: 600 }}>negativos ($…)</span>{' '}
            son tiempo adelantado que la persona debe y <strong>se restan</strong> del total.
            Quien no tiene tarifa cargada aparece con «—» y queda fuera.
          </div>

          <div style={{ marginBottom: 10 }}>
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar por nombre o ID…"
              style={{ ...fieldStyle, width: 260 }}
            />
          </div>

          <div style={{ ...bubbleStyle, padding: 0, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={th}><SortableHeader label="Empleado" sortKey="name" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} /></th>
                  <th style={th}><SortableHeader label="ID" sortKey="external_id" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} /></th>
                  <th style={th}><SortableHeader label="Estado" sortKey="is_active" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} /></th>
                  <th style={{ ...th, textAlign: 'right' }}><SortableHeader label="Vac. saldo" sortKey="vac_balance" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" /></th>
                  <th style={{ ...th, textAlign: 'right' }}>Vac. reserv.</th>
                  <th style={{ ...th, textAlign: 'right' }}><SortableHeader label="Enf. saldo" sortKey="enf_balance" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" /></th>
                  <th style={{ ...th, textAlign: 'right' }}><SortableHeader label="Tarifa" sortKey="hourly_rate" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" /></th>
                  <th style={{ ...th, textAlign: 'right' }}><SortableHeader label="Costo vac." sortKey="vacation_liability" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" /></th>
                  <th style={{ ...th, textAlign: 'right' }}><SortableHeader label="Costo enf." sortKey="sick_liability" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" /></th>
                  <th style={{ ...th, textAlign: 'right' }}><SortableHeader label="Total" sortKey="total_liability" currentKey={sortKey} currentDir={sortDir} onSort={toggleSort} align="right" /></th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map(r => (
                  <tr key={r.persona_id} style={{ background: r.is_active ? undefined : '#fafafa' }}>
                    <td style={td}>{r.name}</td>
                    <td style={{ ...td, color: '#64748b', fontFamily: 'monospace', fontSize: 11.5 }}>{r.external_id || '—'}</td>
                    <td style={td}>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10,
                        background: r.is_active ? '#dcfce7' : '#f1f5f9',
                        color: r.is_active ? '#16a34a' : '#64748b',
                      }}>{r.is_active ? 'Activo' : 'Inactivo'}</span>
                    </td>
                    <td style={{ ...tdNum, color: horasColor(r.vac_balance) }}>{horas(r.vac_balance)}</td>
                    <td style={{ ...tdNum, color: r.vac_reserved > 0 ? '#a16207' : '#94a3b8' }}>{horas(r.vac_reserved)}</td>
                    <td style={{ ...tdNum, color: horasColor(r.enf_balance) }}>{horas(r.enf_balance)}</td>
                    <td style={{ ...tdNum, color: r.hourly_rate === null ? '#94a3b8' : NAVY }}>
                      {r.hourly_rate === null ? '—' : money(r.hourly_rate)}
                    </td>
                    <td style={{ ...tdNum, color: moneyColor(r.vacation_liability) }}>
                      {money(r.vacation_liability)}
                    </td>
                    {/* ⚠ En un INACTIVO el «—» no es "falta el dato": su enfermedad no es
                        pasivo. Se distingue del «—» por falta de tarifa con el título. */}
                    <td
                      style={{ ...tdNum, color: moneyColor(r.sick_liability) }}
                      title={!r.is_active ? 'La enfermedad de un inactivo no cuenta: se reinicia al reactivar' : undefined}
                    >
                      {money(r.sick_liability)}
                    </td>
                    <td style={{ ...tdNum, fontWeight: 700, color: moneyColor(r.total_liability) }}>
                      {money(r.total_liability)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <PaginationBar
            page={page} pageSize={pageSize} totalPages={totalPages} totalItems={totalItems}
            onPageChange={setPage} onPageSizeChange={setPageSize}
          />
        </>
      )}
    </div>
  );
}
